import Titulo from '../../components/tipografia/Titulo';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Icon from '@mdi/react';
import { mdiMagnify } from '@mdi/js';
import { DragDropContext } from 'react-beautiful-dnd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import SalaEspera from './SalaEspera';
import { toast } from 'react-toastify';
import Grid, { GridItem } from '../../components/layout/Grid';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import { useDebounce } from '../chat/useDebounce';
import FiltroGrupoUsuario from '../../components/Filtros/FiltroGrupoUsuario';
import Modal from '../../components/layout/Modal';
import { io } from 'socket.io-client';

const HORAS_24 = 24;

// A rota de transferência exige um motivo; o quadro não tem onde pedi-lo.
const MOTIVO_QUADRO = 'Transferido pelo quadro de filas de atendimento';

const isEspera24h = (espera: any): boolean => {
  if (!espera?.data_criacao) return false;
  return (Date.now() - new Date(espera.data_criacao).getTime()) / (1000 * 60 * 60) > HORAS_24;
};

type Query = {
  grupo_id?: [string, any];
  online?: [string, any];
};

const reorder = (list: any, startIndex: number, endIndex: number): any => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const move = (
  source: any,
  destination: any,
  droppableSource: any,
  droppableDestination: any,
): any => {
  const sourceClone = Array.isArray(source) ? Array.from(source) : [];
  const destClone = Array.isArray(destination) ? Array.from(destination) : [];
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  if (removed) destClone.splice(droppableDestination.index, 0, removed);

  const result: any = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

type DroppableTarget =
  | { type: 'waiting' }
  | { type: 'group'; id: number }
  | { type: 'user'; id: number }
  | { type: 'unknown' };

const parseDroppableId = (droppableId: string): DroppableTarget => {
  if (droppableId === 'aguardando_atendente') {
    return { type: 'waiting' };
  }

  if (droppableId.startsWith('grupo-')) {
    const [, rawId] = droppableId.split('-');
    const id = Number(rawId);
    return Number.isFinite(id) ? { type: 'group', id } : { type: 'unknown' };
  }

  if (droppableId.startsWith('em_atendimento-')) {
    const [, rawId] = droppableId.split('-');
    const id = Number(rawId);
    return Number.isFinite(id) ? { type: 'user', id } : { type: 'unknown' };
  }

  return { type: 'unknown' };
};

type PageProps = {};

type FilterProps = {
  somente_online: boolean;
  grupo_id: number[];
};

type Espera24hPendente = {
  esperaId: number;
  grupoId?: number;
  usuarioId?: number;
  canalId?: number;
};

const ModalRedirecionar24h = ({
  espera,
  onClose,
  onConfirm,
}: {
  espera: Espera24hPendente | null;
  onClose: () => void;
  onConfirm: (mensagemProgramadaId: number) => void;
}) => {
  const [idMensagem, setIdMensagem] = useState<string | null>(null);

  const mensagensQuery = useQuery(
    ['mensagem_programada_fila', espera?.canalId],
    async () => {
      const fi = JSON.stringify({ estado: ['eq', 'aprovada'], ...(espera?.canalId ? { canal_id: ['eq', espera.canalId] } : {}) });
      const res = await fetch(`/api/mensagem_programada?fi=${encodeURIComponent(fi)}`);
      return res.json();
    },
    { enabled: !!espera, staleTime: 30000 },
  );

  const mensagensTexto = (mensagensQuery.data?.result ?? [])
    .filter((msg: any) => !msg.botoes?.length)
    .map((msg: any) => ({ id: String(msg.id), label: msg.nome }));

  const mensagem = (mensagensQuery.data?.result ?? []).find((msg: any) => String(msg.id) === idMensagem);

  const handleClose = () => {
    setIdMensagem(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!idMensagem) return;
    onConfirm(+idMensagem);
    setIdMensagem(null);
  };

  return (
    <Modal
      title="Redirecionar — cliente em espera há mais de 24h"
      show={!!espera}
      onClose={handleClose}
      onConfirm={handleConfirm}
      confirmProps={{ label: 'Enviar e redirecionar', disabled: !idMensagem }}
      cancelProps={{ label: 'Cancelar' }}
    >
      <VBox gap="8px">
        <p style={{ margin: 0 }}>
          Este cliente está em espera há mais de 24 horas. Para retomar o contato pelo WhatsApp, é necessário
          enviar um modelo de mensagem aprovado antes de redirecionar.
        </p>
        {mensagensQuery.isLoading && <p>Carregando modelos...</p>}
        {!mensagensQuery.isLoading && (
          <CampoSeletor
            label="Modelo de mensagem a enviar"
            options={mensagensTexto}
            value={idMensagem ?? undefined}
            onChange={setIdMensagem}
            optionLabel="label"
            optionValue="id"
          />
        )}
        {mensagem && (
          <div style={{ marginTop: '8px', padding: '8px', background: 'var(--tc-color-gray-100)', borderRadius: '8px', fontSize: '0.9rem', fontStyle: 'italic' }}>
            {mensagem.conteudo}
          </div>
        )}
      </VBox>
    </Modal>
  );
};

const Page = ({ }: PageProps) => {
  const [dndList, setDndList] = useState<any>({});
  const [dragItem, setDragItem] = useState(null);
  const [espera24hPendente, setEspera24hPendente] = useState<Espera24hPendente | null>(null);

  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500);

  const [filter, setFilter] = useState<FilterProps>({
    somente_online: true,
    grupo_id: [],
  });

  const queryUsers = useQuery(
    ['usuarios', filter, debouncedSearch],
    async () => {
      const fi: Query = {};
      const q: Record<string, string> = {};

      if (filter?.grupo_id) {
        fi.grupo_id = ['in', filter.grupo_id];
      }

      if (filter?.somente_online) {
        q.somente_online = 'true';
      }

      const res = await fetch(
        `/api/usuario?t=200&txt=${encodeURIComponent(debouncedSearch)}&fi=${encodeURIComponent(JSON.stringify(fi))}&q=${encodeURIComponent(JSON.stringify(q))}`,
      );
      return res.json();
    },
    {
      staleTime: 1000 * 60 * 15,
      enabled: !dragItem,
    },
  );

  const queryGroups = useQuery(
    ['grupos'],
    async () => {
      const res = await fetch(`/api/usuario_grupo/box`);
      return res.json();
    },
    {
      staleTime: 1000 * 60 * 15,
      enabled: !dragItem,
    },
  );

  const queryList = useQuery(
    ['chat/abertos'],
    async () => {
      const res = await fetch(`/api/chat/abertos`);
      return res.json();
    },
    {
      enabled: queryUsers.isSuccess && !dragItem,
      staleTime: 1000 * 10,
      onSuccess(data) {
        buildDndList(data.result);
      },
      onSettled(data, error) {
        console.log('onSettled', data, error);
      },
    },
  );

  const queryEsperas = useQuery(
    ['fluxo_resumo_agendado/esperas'],
    async () => {
      const res = await fetch(`/api/fluxo_resumo_agendado`);
      return res.json();
    },
    {
      enabled: !dragItem,
      staleTime: 1000 * 15,
    },
  );

  // Mantém o quadro reativo: em vez de depender só do polling/staleTime,
  // reage ao sinal `fila_board.changed` emitido pelo backend sempre que um
  // chat entra/sai da fila, é aceito, expira, ou um atendente fica on/offline.
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Durante o drag as queries ficam com `enabled: false` para não brigar com o
  // estado local otimista, mas `refetch()` do react-query ignora `enabled` —
  // um evento chegando no meio do drag (de OUTRO card) reconstruiria o
  // dndList embaixo do usuário. Por isso os escopos pendentes só são
  // aplicados quando o drag termina.
  const dragItemRef = useRef(dragItem);
  useEffect(() => {
    dragItemRef.current = dragItem;
  }, [dragItem]);

  const pendingEscoposRef = useRef<Set<string>>(new Set());

  const aplicarEscopos = useCallback(
    (escopos: Set<string>) => {
      if (escopos.has('chat')) {
        queryList.refetch();
        queryEsperas.refetch();
      }
      if (escopos.has('presenca')) {
        queryUsers.refetch();
      }
    },
    [queryList, queryEsperas, queryUsers],
  );

  useEffect(() => {
    if (!dragItem && pendingEscoposRef.current.size) {
      const escopos = pendingEscoposRef.current;
      pendingEscoposRef.current = new Set();
      aplicarEscopos(escopos);
    }
  }, [dragItem, aplicarEscopos]);

  useEffect(() => {
    const socket = io('/wsinternal', {
      timeout: 5000,
      autoConnect: true,
      withCredentials: true,
      transports: ['websocket'],
    });

    const onFilaBoardChanged = (data: { escopo?: string }) => {
      const escopo = data?.escopo ?? 'chat';
      if (escopo !== 'chat' && escopo !== 'presenca') return;

      if (dragItemRef.current) {
        pendingEscoposRef.current.add(escopo);
        return;
      }

      if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current);
      refetchTimeoutRef.current = setTimeout(() => {
        aplicarEscopos(new Set([escopo]));
      }, 400);
    };

    socket.on('fila_board.changed', onFilaBoardChanged);

    return () => {
      if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current);
      socket.off('fila_board.changed', onFilaBoardChanged);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chatChgMutator = useMutation(
    async (data: { id: number }) => {
      const res = await fetch(`/api/chat/prioridades`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error('Erro ao alterar prioridade');

      return res.json();
    },
    {
      onSettled: async () => {
        setDragItem(null);
        queryList.refetch();
      },
      onSuccess: async () => {
        toast('Atendimento alterado com sucesso', {
          toastId: 'success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
      },
      onError: async () => {
        toast('Erro ao alterar atendimento. Tente novamente mais tarde.', {
          toastId: 'error',
          position: 'top-center',
          type: 'error',
          autoClose: 5000,
        });
      },
    },
  );

  const assignGroupMutator = useMutation(
    async ({ chatId, groupId }: { chatId: number; groupId: number }) => {
      const res = await fetch(`/api/chat/${chatId}/atendente`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: 'grupo', id: groupId, motivo: MOTIVO_QUADRO }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.msg || 'Erro ao mover atendimento para o grupo');

      return data;
    },
    {
      onSettled: async () => {
        setDragItem(null);
        queryList.refetch();
      },
      onSuccess: async () => {
        toast('Atendimento movido para o grupo', {
          toastId: 'success-group',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
      },
      onError: async (error: any) => {
        toast(error?.message || 'Erro ao mover atendimento para o grupo. Tente novamente.', {
          toastId: 'error-group',
          position: 'top-center',
          type: 'error',
          autoClose: 5000,
        });
      },
    },
  );

  const assignUserMutator = useMutation(
    async ({ chatId, userId }: { chatId: number; userId: number }) => {
      const res = await fetch(`/api/chat/${chatId}/atendente`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: 'usuario', id: userId, motivo: MOTIVO_QUADRO }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.msg || 'Erro ao mover atendimento para o atendente');

      return data;
    },
    {
      onSettled: async () => {
        setDragItem(null);
        queryList.refetch();
      },
      onSuccess: async () => {
        toast('Atendimento alterado com sucesso', {
          toastId: 'success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
      },
      onError: async (error: any) => {
        toast(error?.message || 'Erro ao alterar atendimento. Tente novamente mais tarde.', {
          toastId: 'error',
          position: 'top-center',
          type: 'error',
          autoClose: 5000,
        });
      },
    },
  );

  const redirecionarEsperaMutator = useMutation(
    async ({ esperaId, grupoId, usuarioId, mensagemProgramadaId }: { esperaId: number; grupoId?: number; usuarioId?: number; mensagemProgramadaId?: number }) => {
      const res = await fetch(`/api/fluxo_resumo_agendado/${esperaId}/redirecionar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grupo_id: grupoId, usuario_id: usuarioId, mensagem_programada_id: mensagemProgramadaId }),
      });
      if (!res.ok) throw new Error('Erro ao redirecionar espera');
      return res.json();
    },
    {
      onSettled: async () => {
        setDragItem(null);
        queryList.refetch();
        queryEsperas.refetch();
      },
      onSuccess: async () => {
        toast('Cliente redirecionado com sucesso', {
          toastId: 'success-espera',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
      },
      onError: async () => {
        toast('Erro ao redirecionar. Tente novamente.', {
          toastId: 'error-espera',
          position: 'top-center',
          type: 'error',
          autoClose: 5000,
        });
      },
    },
  );

  const buildDndList = useCallback(
    (list: any) => {
      const itens = Array.isArray(list) ? list : [];
      const esperas = Array.isArray(queryEsperas.data?.result) ? queryEsperas.data.result : [];

      const listas: Record<string, any[]> = {
        aguardando_atendente: itens.filter(
          (it: any) =>
            it.usuario_responsavel_id == null &&
            it.fila_usuario_responsavel_id == null &&
            it.grupo_responsavel_id == null,
        ),
      };

      if (queryGroups?.data?.result) {
        for (const grupo of queryGroups.data.result) {
          const clientesDoGrupo = itens.filter(
            (it: any) =>
              it.grupo_responsavel_id === grupo.id &&
              it.usuario_responsavel_id == null,
          );
          const esperasDoGrupo = esperas
            .filter((e: any) => e.id_grupo === grupo.id)
            .map((e: any) => ({ ...e, _type: 'espera' as const }));
          listas[`grupo-${grupo.id}`] = [...clientesDoGrupo, ...esperasDoGrupo];
        }
      }

      if (queryUsers?.data?.result) {
        for (const user of queryUsers.data.result) {
          listas[`em_atendimento-${user.id}`] = itens.filter(
            (it: any) =>
              it.usuario_responsavel_id === user.id ||
              it.fila_usuario_responsavel_id === user.id,
          );
        }
      }

      setDndList(listas);
    },
    [queryGroups?.data?.result, queryUsers.data?.result, queryEsperas.data?.result],
  );

  useEffect(() => {
    if (queryList.data?.result) {
      buildDndList(queryList.data.result);
    }
  }, [queryList.data?.result, queryEsperas.data?.result, buildDndList]);

  const onDragStart = useCallback(
    (result: any) => {
      setDragItem(dndList?.[result.source.droppableId]?.[result.source.index]);
    },
    [dndList],
  );

  const onDragEnd = useCallback(
    (result: any) => {
      const { source, destination, draggableId } = result;

      if (!destination) {
        setDragItem(null);
        return;
      }

      const destino = parseDroppableId(destination.droppableId);

      if (destino.type === 'unknown') {
        setDragItem(null);
        return;
      }

      if (destino.type === 'group') {
        const grupoDestino = queryGroups.data?.result?.find((g: any) => g.id === destino.id);
        if (grupoDestino?.aceita_atendimento === false) {
          toast('Departamento não aceita atendimentos', {
            toastId: 'error-group-disabled',
            position: 'top-center',
            type: 'error',
            autoClose: 5000,
          });
          setDragItem(null);
          return;
        }
      }

      const isSameDroppable = source.droppableId === destination.droppableId;

      // Item de espera por disponibilidade de departamento
      const isEspera = draggableId.startsWith('espera-');
      if (isEspera) {
        // Esperas não podem voltar para a sala de espera geral nem para o mesmo slot
        if (destino.type === 'waiting' || isSameDroppable) {
          setDragItem(null);
          return;
        }
        const esperaId = Number(draggableId.split('-')[1]);
        if (!Number.isFinite(esperaId)) { setDragItem(null); return; }

        const novoEstado = move(
          dndList[source.droppableId],
          dndList[destination.droppableId],
          source,
          destination,
        );
        setDndList(prev => ({ ...(prev ?? {}), ...(novoEstado ?? {}) }));

        const esperaItem = dndList[source.droppableId]?.[source.index];

        if (isEspera24h(esperaItem) && (destino.type === 'group' || destino.type === 'user')) {
          setEspera24hPendente({
            esperaId,
            grupoId: destino.type === 'group' ? destino.id : undefined,
            usuarioId: destino.type === 'user' ? destino.id : undefined,
            canalId: esperaItem?.canal_id,
          });
          setDragItem(null);
          return;
        }

        if (destino.type === 'group') {
          redirecionarEsperaMutator.mutate({ esperaId, grupoId: destino.id });
        } else if (destino.type === 'user') {
          redirecionarEsperaMutator.mutate({ esperaId, usuarioId: destino.id });
        } else {
          setDragItem(null);
        }
        return;
      }

      const chatId = Number(draggableId.split('-')[1]);

      if (!Number.isFinite(chatId)) {
        setDragItem(null);
        return;
      }

      if (isSameDroppable) {
        const items = reorder(
          dndList[source.droppableId] ?? [],
          source.index,
          destination.index,
        );

        setDndList(prev => ({
          ...prev,
          [source.droppableId]: items,
        }));

        if (destino.type === 'user') {
          chatChgMutator.mutate({
            id: chatId,
            prioridade_atendimento_de: source.index,
            prioridade_atendimento_para: destination.index,
            usuario_responsavel_id: destino.id,
          });
        } else {
          setDragItem(null);
        }

        return;
      }

      const novoEstado = move(
        dndList[source.droppableId],
        dndList[destination.droppableId],
        source,
        destination,
      );

      const destinoItens = novoEstado[destination.droppableId] ?? [];
      if (destinoItens[destination.index]) {
        destinoItens[destination.index] = {
          ...destinoItens[destination.index],
          usuario_responsavel_id:
            destino.type === 'user' ? destino.id : null,
          fila_usuario_responsavel_id:
            destino.type === 'user' ? destino.id : null,
          grupo_responsavel_id:
            destino.type === 'group'
              ? destino.id
              : destino.type === 'waiting'
                ? null
                : destino.type === 'user'
                  ? null
                  : destinoItens[destination.index].grupo_responsavel_id,
          // Soltar num atendente entrega o chat: ele entra em atendimento.
          estado: destino.type === 'user' ? 'atendimento' : 'aguardando_atendente',
        };
        novoEstado[destination.droppableId] = destinoItens;
      }

      setDndList(prev => ({
        ...(prev ?? {}),
        ...(novoEstado ?? {}),
      }));

      if (destino.type === 'user') {
        // /chat/prioridades só reordena chats que JÁ são do atendente; a troca de
        // responsável precisa passar pela rota de transferência.
        assignUserMutator.mutate({ chatId, userId: destino.id });
      } else if (destino.type === 'group') {
        assignGroupMutator.mutate({ chatId, groupId: destino.id });
      } else {
        setDragItem(null);
        queryList.refetch();
      }
    },
    [
      assignGroupMutator,
      assignUserMutator,
      chatChgMutator,
      redirecionarEsperaMutator,
      dndList,
      queryList,
      queryGroups.data,
    ],
  );

  // Calculate which groups have at least one online attendant
  const gruposComAtendentesOnline = useMemo(() => {
    const grupos = new Set<number>();
    if (queryUsers.data?.result) {
      queryUsers.data.result.forEach((user: any) => {
        if (user.online && user.grupos) {
          user.grupos.forEach((grupo: any) => {
            grupos.add(grupo.id);
          });
        }
      });
    }
    return grupos;
  }, [queryUsers.data?.result]);

  return (
    <LayoutUsuario>
      <ModalRedirecionar24h
        espera={espera24hPendente}
        onClose={() => setEspera24hPendente(null)}
        onConfirm={(mensagemProgramadaId) => {
          if (!espera24hPendente) return;
          redirecionarEsperaMutator.mutate({
            esperaId: espera24hPendente.esperaId,
            grupoId: espera24hPendente.grupoId,
            usuarioId: espera24hPendente.usuarioId,
            mensagemProgramadaId,
          });
          setEspera24hPendente(null);
        }}
      />
      <div style={{ overflow: 'auto' }}>
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
            {/* Header */}
            <HBox>
              <VBox>
                <Titulo>Filas de atendimento</Titulo>
                <Descricao>
                  Gerencie aqui quais clientes serão atendidos por quais
                  atendentes
                </Descricao>
              </VBox>
              {/* <HBox gap="12px" className={styles.headerAcoes}>
                <Botao variant="outline-primary">Ação secundária</Botao>
                <Botao variant="primary">Ação principal</Botao>
              </HBox> */}
            </HBox>

            {/* Filtros */}
            <Grid>
              <GridItem sm={12} md={4}>
                <CampoTexto
                  value={search}
                  onChange={setSearch}
                  prepend={
                    <Icon
                      path={mdiMagnify}
                      size={1}
                      color="var(--tc-color-gray-500)"
                    />
                  }
                  placeholder="Pesquisar"
                />
              </GridItem>
              <GridItem sm={12} md={4}>
                <FiltroGrupoUsuario
                  value={filter.grupo_id.map(it => it.toString())}
                  onChange={value =>
                    setFilter({
                      ...filter,
                      grupo_id: value.map(it => parseInt(it)),
                    })
                  }
                  disableQuery={!!dragItem}
                />
              </GridItem>
              <GridItem sm={12} md={4}>
                <CampoSeletor
                  options={[
                    { id: 'online', label: 'Somente atendentes online' },
                    { id: 'all', label: 'Todos os atendentes' },
                  ]}
                  value={filter.somente_online ? 'online' : 'all'}
                  onChange={value =>
                    setFilter({
                      ...filter,
                      somente_online: value === 'online',
                    })
                  }
                  noSearch
                />
              </GridItem>
            </Grid>

            {/* Conteudo */}
            <VBox gap="16px" style={{ overflow: 'auto' }}>
              <SalaEspera
                droppableId="aguardando_atendente"
                dragItem={dragItem}
                nome="Sala de espera"
                descricao="Atend. automático"
                clientes={dndList['aguardando_atendente']}
              />

              {queryGroups.data?.result?.map((grupo: any) => (
                <SalaEspera
                  key={`grupo-${grupo.id}`}
                  droppableId={`grupo-${grupo.id}`}
                  dragItem={dragItem}
                  nome={grupo.nome}
                  descricao={grupo.descricao ?? 'Grupo / Departamento'}
                  clientes={dndList[`grupo-${grupo.id}`]}
                  hasOnlineAttendants={gruposComAtendentesOnline.has(grupo.id)}
                  aceitaAtendimento={grupo.aceita_atendimento !== false}
                  dentroExpediente={grupo.dentro_expediente !== false}
                />
              ))}

              {queryUsers.data?.result?.map((user: any) => (
                <SalaEspera
                  key={`card-${user.id}`}
                  dragItem={dragItem}
                  nome={user.nome}
                  online={user.online}
                  droppableId={`em_atendimento-${user.id}`}
                  descricao={`Depto. ${user.grupos?.map((g: any) => g.nome)?.join(', ')}`}
                  clientes={dndList[`em_atendimento-${user.id}`]}
                  requiresGrupo
                />
              ))}
            </VBox>
          </VBox>
        </DragDropContext>
      </div>
    </LayoutUsuario>
  );
};

export default Page;
